import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import WrapperComponent from '../commonComponents/WrapperComponent';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { chatbotUrl } from '../../util/config';

// ── Status + Mode config ─────────────────────────────────────────────────────
const STATUS_META = {
    0: { label: 'Draft',     color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: 'bi-clock' },
    1: { label: 'Published', color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0', icon: 'bi-check-circle' },
    2: { label: 'Running',   color: '#1565c0', bg: '#eff6ff', border: '#bfdbfe', icon: 'bi-lightning' },
    3: { label: 'Disabled',  color: '#6b7280', bg: '#f9fafb', border: '#e5e7eb', icon: 'bi-x-circle' },
};

const MODE_META = {
    strict:   { label: 'Strict Flow', color: '#dc2626', bg: '#fef2f2' },
    hybrid:   { label: 'Hybrid',      color: '#1565c0', bg: '#eff6ff' },
    rag_only: { label: 'RAG Only',    color: '#16a34a', bg: '#f0fdf4' },
};

// ── Confirm Modal ────────────────────────────────────────────────────────────
const ConfirmModal = ({ open, onClose, onConfirm, loading, title, message, confirmText }) => {
    if (!open) return null;
    return (
        <div style={ms.overlay}>
            <div style={ms.box}>
                <div style={ms.header}>
                    <div style={ms.warningIcon}><i className="bi bi-exclamation-triangle" style={{ fontSize: 16, color: '#dc2626' }}></i></div>
                    <div><h6 style={ms.title}>{title}</h6></div>
                    <button style={ms.closeBtn} onClick={onClose}><i className="bi bi-x-lg"></i></button>
                </div>
                <div style={ms.body}><p style={ms.msg}>{message}</p></div>
                <div style={ms.footer}>
                    <button style={ms.btnSecondary} onClick={onClose} disabled={loading}>Cancel</button>
                    <button style={ms.btnDanger} onClick={onConfirm} disabled={loading}>
                        {loading ? <><span style={ms.spinner}></span> Deleting…</> : confirmText || 'Confirm'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ms = {
    overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050 },
    box: { background: 'white', borderRadius: '12px', width: '100%', maxWidth: '400px', boxShadow: '0 20px 40px rgba(0,0,0,0.2)', overflow: 'hidden' },
    header: { display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid #f3f4f6', background: '#fff7f7' },
    warningIcon: { width: 38, height: 38, borderRadius: 10, background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    title: { margin: 0, fontSize: 15, fontWeight: 700, color: '#111827' },
    closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 13, padding: 0, marginLeft: 'auto' },
    body: { padding: 20 },
    msg: { fontSize: 14, color: '#6b7280', margin: 0, lineHeight: 1.6 },
    footer: { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid #f3f4f6' },
    btnSecondary: { padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    btnDanger: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    spinner: { width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' },
};

// ── Action Menu ──────────────────────────────────────────────────────────────
const ActionMenu = ({ flow, onEdit, onDelete, onToggle }) => {
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
    const ref = useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleOpen = () => {
        if (!open && ref.current) {
            const rect = ref.current.getBoundingClientRect();
            const menuWidth = 170;
            const menuHeight = 120;
            const spaceBelow = window.innerHeight - rect.bottom;
            const top = spaceBelow < menuHeight
                ? rect.top - menuHeight
                : rect.bottom;
            const left = rect.left - menuWidth + rect.width;
            setMenuPos({ top, left });
        }
        setOpen(v => !v);
    };

    const id = flow.id || flow._id;
    const isDisabled = Number(flow.status) === 3;

    return (
        <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
            <button style={s.menuTrigger} onClick={() => setOpen(v => !v)}>
                <i className="bi bi-three-dots-vertical"></i>
            </button>
            {open && (
                <div style={{ ...s.menuBox, position: 'absolute', right: 0, top: '36px', zIndex: 100 }}>
                    <button style={s.menuItem} onClick={() => { onEdit(id); setOpen(false); }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                        <span style={{ ...s.menuIcon, background: '#eff6ff', color: '#1565c0' }}><i className="bi bi-pencil-square"></i></span>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#1565c0' }}>Edit Flow</span>
                    </button>
                    <button style={s.menuItem} onClick={() => { onToggle(flow); setOpen(false); }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                        <span style={{ ...s.menuIcon, background: isDisabled ? '#f0fdf4' : '#fffbeb', color: isDisabled ? '#16a34a' : '#d97706' }}>
                            <i className={`bi ${isDisabled ? 'bi-play-circle' : 'bi-pause-circle'}`}></i>
                        </span>
                        <span style={{ fontSize: 13, fontWeight: 500, color: isDisabled ? '#16a34a' : '#d97706' }}>
                            {isDisabled ? 'Enable' : 'Disable'}
                        </span>
                    </button>
                    <div style={{ height: 1, background: '#f3f4f6', margin: '4px 0' }}></div>
                    <button style={s.menuItem} onClick={() => { onDelete(flow); setOpen(false); }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fef2f2'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                        <span style={{ ...s.menuIcon, background: '#fee2e2', color: '#dc2626' }}><i className="bi bi-trash"></i></span>
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#dc2626' }}>Delete</span>
                    </button>
                </div>
            )}
        </div>
    );
};

// ── Flow Card ────────────────────────────────────────────────────────────────
const FlowCard = ({ flow, onEdit, onDelete, onToggle }) => {
    const id = flow.id || flow._id;
    const status = Number(flow.status ?? 0);
    const sm = STATUS_META[status] || STATUS_META[0];
    const mode = flow.config?.mode || 'hybrid';
    const mm = MODE_META[mode] || MODE_META.hybrid;
    const nodeCount = flow.node_count || flow.metadata?.nodes?.length || 0;
    const ragTags = flow.config?.rag_tags || [];

    return (
        <div style={s.card}
            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-3px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(21,101,192,0.1)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 1px 6px rgba(0,0,0,0.06)'; }}>

            {/* Header */}
            <div style={s.cardHeader}>
                <div style={s.cardIconWrap}>
                    <i className="bi bi-diagram-3" style={{ fontSize: 16, color: '#1565c0' }}></i>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={s.cardName}>{flow.flow_name || 'Untitled Flow'}</p>
                    <p style={s.cardId}>{id?.slice(-8)}</p>
                </div>
                <ActionMenu flow={flow} onEdit={onEdit} onDelete={onDelete} onToggle={onToggle} />
            </div>

            {/* Badges */}
            <div style={s.badgeRow}>
                <span style={{ ...s.badge, background: sm.bg, color: sm.color, border: `1px solid ${sm.border}` }}>
                    <i className={`bi ${sm.icon}`} style={{ marginRight: 4, fontSize: 10 }}></i>{sm.label}
                </span>
                <span style={{ ...s.badge, background: mm.bg, color: mm.color }}>{mm.label}</span>
                {flow.config?.channel && (
                    <span style={{ ...s.badge, background: '#f9fafb', color: '#6b7280', border: '1px solid #e5e7eb' }}>
                        {flow.config.channel}
                    </span>
                )}
            </div>

            {/* RAG Tags */}
            {ragTags.length > 0 && (
                <div style={s.tagRow}>
                    {ragTags.slice(0, 3).map(t => (
                        <span key={t} style={s.ragTag}>{t}</span>
                    ))}
                    {ragTags.length > 3 && <span style={{ fontSize: 10, color: '#9ca3af' }}>+{ragTags.length - 3}</span>}
                </div>
            )}

            {/* Footer */}
            <div style={s.cardFooter}>
                <span style={s.cardMeta}><i className="bi bi-node-plus" style={{ marginRight: 4 }}></i>{nodeCount} nodes</span>
                <span style={s.cardMeta}>
                    {flow.created_at ? new Date(flow.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                </span>
            </div>

            {/* Edit button */}
            <button onClick={() => onEdit(id)} style={s.editBtn}>Edit →</button>
        </div>
    );
};

// ── Main Component ───────────────────────────────────────────────────────────
export default function Flows() {
    const navigate = useNavigate();
    const [flows, setFlows] = useState([]);
    const [search, setSearch] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [deleteFlow, setDeleteFlow] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const debounceRef = useRef(null);
    const limit = 9;

    const fetchFlows = async (page = 1, searchVal = search) => {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        try {
            const params = new URLSearchParams({ limit, skip: (page - 1) * limit });
            if (searchVal) params.append('flow_name', searchVal);
            const response = await fetch(`${chatbotUrl}/flows?${params}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to fetch flows');
            const data = await response.json();
            setFlows(data.data?.items || data.data || []);
            setTotalCount(data.data?.total || 0);
        } catch (err) {
            setError('Failed to load flows. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { fetchFlows(1); }, []);

    const handleSearchChange = (val) => {
        setSearch(val);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            setCurrentPage(1);
            fetchFlows(1, val);
        }, 400);
    };

    const handleDelete = async () => {
        setDeleteLoading(true);
        const token = localStorage.getItem('token');
        try {
            const id = deleteFlow.id || deleteFlow._id;
            const response = await fetch(`${chatbotUrl}/flows/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Failed to delete flow');
            toast.success('Flow deleted successfully', { autoClose: 2000 });
            setDeleteFlow(null);
            fetchFlows(currentPage);
        } catch (err) {
            toast.error('Failed to delete flow. Please try again.', { autoClose: 3000 });
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleToggle = async (flow) => {
        const token = localStorage.getItem('token');
        const id = flow.id || flow._id;
        const target = Number(flow.status) === 3 ? 1 : 3;
        try {
            const response = await fetch(`${chatbotUrl}/flows/${id}`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ status: target }),
            });
            if (!response.ok) throw new Error('Failed to update flow');
            toast.success(target === 1 ? 'Flow enabled' : 'Flow disabled', { autoClose: 2000 });
            fetchFlows(currentPage);
        } catch (err) {
            toast.error('Failed to update flow. Please try again.', { autoClose: 3000 });
        }
    };

    const totalPages = Math.ceil(totalCount / limit);

    return (
        <WrapperComponent>
            <div style={s.page}>
                {/* Page header */}
                <div style={s.pageHeader}>
                    <div>
                        <h5 style={s.pageTitle}>Flow Builder</h5>
                        <p style={s.pageSubtitle}>Design conversation flows for your WhatsApp chatbot</p>
                    </div>
                    <button style={s.btnPrimary} onClick={() => navigate('/flows/builder?new=true')}>
                        <i className="bi bi-plus-lg" style={{ marginRight: 6 }}></i>Create Flow
                    </button>
                </div>

                {/* Search */}
                <div style={s.searchRow}>
                    <div style={s.searchWrapper}>
                        <i className="bi bi-search" style={s.searchIcon}></i>
                        <input
                            style={s.searchInput}
                            placeholder="Search flows..."
                            value={search}
                            onChange={e => handleSearchChange(e.target.value)}
                        />
                    </div>
                    <span style={s.resultCount}>{totalCount} flow{totalCount !== 1 ? 's' : ''}</span>
                </div>

                {error && (
                    <div style={s.errorBanner}>
                        <i className="bi bi-exclamation-circle" style={{ marginRight: 8 }}></i>{error}
                    </div>
                )}

                {/* Grid */}
                {isLoading ? (
                    <div style={s.centerState}>
                        <div className="spinner-border text-primary" style={{ width: '2rem', height: '2rem' }}></div>
                        <p style={s.stateText}>Loading flows…</p>
                    </div>
                ) : (
                    <div style={s.grid}>
                        {/* Create card */}
                        <div style={s.createCard}
                            onClick={() => navigate('/flows/builder?new=true')}
                            onMouseEnter={e => { e.currentTarget.style.background = '#eff6ff'; e.currentTarget.style.borderColor = '#1565c0'; }}
                            onMouseLeave={e => { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#bfdbfe'; }}>
                            <div style={s.createIcon}><i className="bi bi-plus-lg" style={{ fontSize: 22, color: 'white' }}></i></div>
                            <p style={s.createTitle}>Create New Flow</p>
                            <p style={s.createSubtitle}>AI generate, template, or from scratch</p>
                        </div>

                        {flows.map(flow => (
                            <FlowCard
                                key={flow.id || flow._id}
                                flow={flow}
                                onEdit={id => navigate(`/flows/builder?flow=${id}`)}
                                onDelete={setDeleteFlow}
                                onToggle={handleToggle}
                            />
                        ))}
                    </div>
                )}

                {!isLoading && flows.length === 0 && !error && (
                    <div style={s.centerState}>
                        <i className="bi bi-diagram-3" style={{ fontSize: 40, color: '#d1d5db' }}></i>
                        <p style={s.stateText}>No flows yet. Create your first flow to get started.</p>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={s.pagination}>
                        <button style={s.pageBtn} disabled={currentPage <= 1}
                            onClick={() => { setCurrentPage(p => p - 1); fetchFlows(currentPage - 1); }}>
                            <i className="bi bi-chevron-left"></i> Prev
                        </button>
                        <span style={s.pageInfo}>{currentPage} / {totalPages}</span>
                        <button style={s.pageBtn} disabled={currentPage >= totalPages}
                            onClick={() => { setCurrentPage(p => p + 1); fetchFlows(currentPage + 1); }}>
                            Next <i className="bi bi-chevron-right"></i>
                        </button>
                    </div>
                )}

                <ToastContainer />

                <ConfirmModal
                    open={!!deleteFlow}
                    onClose={() => setDeleteFlow(null)}
                    onConfirm={handleDelete}
                    loading={deleteLoading}
                    title="Delete Flow"
                    message={`Delete "${deleteFlow?.flow_name}"? If active agents are using this flow, deletion will fail.`}
                    confirmText="Delete Flow"
                />
            </div>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </WrapperComponent>
    );
}

const s = {
    page: { display: 'flex', flexDirection: 'column', gap: 16, fontFamily: "'Segoe UI', system-ui, sans-serif" },
    pageHeader: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 },
    pageTitle: { margin: 0, fontSize: 20, fontWeight: 700, color: '#111827' },
    pageSubtitle: { margin: '2px 0 0', fontSize: 13, color: '#6b7280' },
    searchRow: { display: 'flex', alignItems: 'center', gap: 12 },
    searchWrapper: { position: 'relative', flex: 1, maxWidth: 360 },
    searchIcon: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: 14 },
    searchInput: { width: '100%', padding: '9px 12px 9px 36px', fontSize: 14, border: '1.5px solid #e5e7eb', borderRadius: 8, outline: 'none', boxSizing: 'border-box', color: '#111827' },
    resultCount: { fontSize: 13, color: '#6b7280', whiteSpace: 'nowrap' },
    errorBanner: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#dc2626', display: 'flex', alignItems: 'center' },
    centerState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '60px 20px' },
    stateText: { fontSize: 14, color: '#6b7280', margin: 0 },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 },
    createCard: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, border: '2px dashed #bfdbfe', borderRadius: 12, padding: 32, cursor: 'pointer', background: '#f8fafc', transition: 'all 0.2s', minHeight: 180 },
    createIcon: { width: 48, height: 48, borderRadius: 12, background: 'linear-gradient(to right, #2388e0, #1565c0)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    createTitle: { fontWeight: 700, fontSize: 14, color: '#111827', margin: 0 },
    createSubtitle: { fontSize: 12, color: '#6b7280', margin: 0, textAlign: 'center' },
    card: { background: 'white', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', transition: 'all 0.15s', position: 'relative', display: 'flex', flexDirection: 'column', gap: 10 },
    cardHeader: { display: 'flex', alignItems: 'center', gap: 10 },
    cardIconWrap: { width: 36, height: 36, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
    cardName: { fontWeight: 700, fontSize: 14, color: '#111827', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
    cardId: { fontSize: 11, color: '#9ca3af', margin: 0, fontFamily: 'monospace' },
    badgeRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    badge: { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, display: 'inline-flex', alignItems: 'center' },
    tagRow: { display: 'flex', gap: 4, flexWrap: 'wrap' },
    ragTag: { fontSize: 10, padding: '1px 6px', borderRadius: 10, background: '#eff6ff', color: '#1565c0', fontFamily: 'monospace' },
    cardFooter: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 10, borderTop: '1px solid #f3f4f6' },
    cardMeta: { fontSize: 11, color: '#9ca3af', display: 'flex', alignItems: 'center' },
    editBtn: { position: 'absolute', bottom: 14, right: 14, padding: '5px 12px', fontSize: 12, fontWeight: 600, color: '#1565c0', background: '#eff6ff', border: 'none', borderRadius: 6, cursor: 'pointer' },
    menuTrigger: { background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, width: 28, height: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#6b7280', fontSize: 13, flexShrink: 0 },
    menuBox: { background: 'white', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #f3f4f6', minWidth: 170, padding: 6 },
    menuItem: { display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '8px 10px', border: 'none', background: 'white', borderRadius: 6, cursor: 'pointer', textAlign: 'left' },
    menuIcon: { width: 26, height: 26, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, flexShrink: 0 },
    pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 8 },
    pageBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '6px 14px', fontSize: 12, fontWeight: 600, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer', color: '#374151' },
    pageInfo: { fontSize: 12, color: '#6b7280' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', padding: '8px 16px', background: 'linear-gradient(to right, #2388e0, #1565c0)', color: 'white', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
};
